Math.clamp = (x, a, b) -> if x < a then a else if x > b then b else x
Math.lerp = (a, b, t) -> a*(1 - t) + b*t
Math.len = (x, y) -> Math.sqrt x*x + y*y
Math.len2 = (x, y) -> x*x + y*y

Math.smoothstep = (edge0, edge1, x) ->
	t = Math.clamp (x - edge0)/(edge1 - edge0), 0, 1
	t*t*(3 - 2*t)

Math.smoothpeak = (v0, v1, v2, x) ->
	t = Math.clamp Math.min(x/v0, (v2 - x)/(v2 - v1)), 0, 1
	t*t*(3 - 2*t)

Math.bitMask = (bits) ->
	bits |= bits >> 1
	bits |= bits >> 2
	bits |= bits >> 4
	bits |= bits >> 8
	bits |= bits >> 16

Math.nextPow2 = (v) -> 1 + Math.bitMask Math.round v - 1

Math.lerpAngle = (a, b, t, limit = Math.PI) ->
	if Math.abs(b - a) < limit
		a += (b - a) * t
	else
		if a < b
			a += limit * 2
		else
			b += limit * 2
		a += (b - a) * t
		if a > limit
			a -= limit * 2
	return a

Math.euler4 = (ax, ay, az, x, y, z, scale = 1) ->
	cx = Math.cos(ax)
	sx = Math.sin(ax)
	cy = Math.cos(ay)
	sy = Math.sin(ay)
	cz = Math.cos(az)
	sz = Math.sin(az)
	[cy*cz*scale, cy*sz*scale, -sy*scale, 0,
		(sx*sy*cz - cx*sz)*scale, (sx*sy*sz + cx*cz)*scale, cy*sx*scale, 0,
		(cx*sy*cz + sx*sz)*scale, (cx*sy*sz - sx*cz)*scale, cy*cx*scale, 0,
		x, y, z, 1]

Math.ortho = (l, t, r, b, n, f) -> [2/(r-l),0,0,0, 0,2/(t-b),0,0, 0,0,-2/(f-n),0, -(r+l)/(r-l),-(t+b)/(t-b),-(f+n)/(f-n),1]
Math.frustum = (w, h, n, f) -> [2*n/w,0,0,0, 0,-2*n/h,0,0, 0,0,-(n+f)/(f-n),-1, 0,0,-2*n*f/(f-n),0]
Math.frustumZ = (w, h, n, f, z) -> [2*n/w,0,0,0, 0,-2*n/h,0,0, 0,0,-(n+f)/(f-n),-1, 0,0,((n+f)*z - 2*n*f)/(f-n),z]

Math.m4v3 = (m4, v3) -> [
	m4[0]*v3[0] + m4[4]*v3[1] + m4[8]*v3[2]
	m4[1]*v3[0] + m4[5]*v3[1] + m4[9]*v3[2]
	m4[2]*v3[0] + m4[6]*v3[1] + m4[10]*v3[2]]

vectorLen = (v) -> Math.sqrt v[0]*v[0] + v[1]*v[1] + v[2]*v[2]
vectorAdd = (a, b) -> [a[0] + b[0], a[1] + b[1], a[2] + b[2]]
vectorSub = (a, b) -> [a[0] - b[0], a[1] - b[1], a[2] - b[2]]
Math.scale3 = (a, f) -> [a[0]*f, a[1]*f, a[2]*f]
vectorDot = (a, b) -> a[0]*b[0] + a[1]*b[1] + a[2]*b[2]
vectorCross = (a, b) -> [a[1]*b[2] - a[2]*b[1], a[2]*b[0] - a[0]*b[2], a[0]*b[1] - a[1]*b[0]]
vectorFaceNormal = (a, b, c) -> vectorCross vectorSub(b, a), vectorSub(c, a)
vectorNormalized = (v) -> Math.scale3 v, 1/vectorLen(v)

class Polyhedron
	constructor: (properties) -> {@verts, @faces} = properties

	clone: -> new Polyhedron verts: @verts.slice(0), faces: @faces.slice(0)

	subdivided: (midVertex) ->
		edgeKey = (a, b) -> if a < b then "#{a}.#{b}" else "#{b}.#{a}"
		edgeMap = {}
		newVerts = @verts.slice 0
		addMid = (a, b) ->
			return x if x = edgeMap[edgeKey a, b]
			newVerts.push midVertex newVerts[a], newVerts[b]
			edgeMap[edgeKey a, b] = newVerts.length - 1
		newFaces = []
		for [a, b, c] in @faces
			ab = addMid a, b
			bc = addMid b, c
			ca = addMid c, a
			newFaces.push [a,ab,ca], [b,bc,ab], [c,ca,bc], [ab,bc,ca]
		new Polyhedron verts: newVerts, faces: newFaces

	deform: (modifyVertex) ->
		@verts[i] = modifyVertex(v) for v, i in @verts
		@

	calculateNormals: ->
		v[3..5] = [0, 0, 0] for v in @verts
		for [a, b, c] in @faces
			n = vectorNormalized vectorFaceNormal @verts[a], @verts[b], @verts[c]
			for i in [a, b, c]
				@verts[i][3] += n[0]
				@verts[i][4] += n[1]
				@verts[i][5] += n[2]
		v[3..5] = vectorNormalized v[3..5] for v in @verts
		@

icoRadius = vectorNormalized [1, (1 + Math.sqrt(5))*0.5, 0]

icosahedron = new Polyhedron
	verts: [
		[-icoRadius[0],icoRadius[1],0], [icoRadius[0],icoRadius[1],0], [-icoRadius[0],-icoRadius[1],0], [icoRadius[0],-icoRadius[1],0]
		[0,-icoRadius[0],icoRadius[1]], [0,icoRadius[0],icoRadius[1]], [0,-icoRadius[0],-icoRadius[1]], [0,icoRadius[0],-icoRadius[1]]
		[icoRadius[1],0,-icoRadius[0]], [icoRadius[1],0,icoRadius[0]], [-icoRadius[1],0,-icoRadius[0]], [-icoRadius[1],0,icoRadius[0]]]
	faces: [
		[0,11,5], [0,5,1], [0,1,7], [0,7,10], [0,10,11], [3,9,4], [3,4,2], [3,2,6], [3,6,8], [3,8,9]
		[1,5,9], [5,11,4], [11,10,2], [10,7,6], [7,1,8], [4,9,5], [2,4,11], [6,2,10], [8,6,7], [9,8,1]]

Math.icoSphere = (n) ->
	sphere = icosahedron.clone()
	for i in [0...n]
		sphere = sphere.subdivided (v0, v1) ->
			d = vectorLen(v0) + vectorLen(v1)
			p = vectorAdd(v0, v1)
			Math.scale3 p, d*0.5/vectorLen(p)
	sphere



// WEBPACK FOOTER //
// ./client/math.coffee